import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import ConfirmModal from '../confirm-modal'
import { useWeb3Context } from '../../contexts/web3Context'
import { checkIsWalletInAlliance } from '../../common/allianceApi'
import { MIN_LEVEL_JOIN } from './constants'
import type { RootState } from '../../store'

/**
 * Recurring "join an alliance" nudge (owner request): every 5–10 minutes,
 * players at the alliance-join level (5+) who are NOT in an alliance get a
 * confirm modal — OK opens the alliance page, Cancel closes it, and the third
 * action suppresses the nudge for 3 days (per wallet, per device).
 *
 * Never fires on the alliance pages themselves, or over battle/arena screens.
 * Membership is re-checked server-side right before every showing, so the
 * nudge stops by itself the moment the player joins or founds an alliance.
 */

const NUDGE_MIN_DELAY_MS = 5 * 60 * 1000
const NUDGE_MAX_DELAY_MS = 10 * 60 * 1000
const SUPPRESS_MS = 3 * 24 * 60 * 60 * 1000

const suppressKey = (address: string) => `allianceJoinNudge:hideUntil:${address.toLowerCase()}`

function isSuppressed(address: string): boolean {
  try {
    const raw = localStorage.getItem(suppressKey(address))
    return !!raw && Number(raw) > Date.now()
  } catch {
    return false
  }
}

function suppressFor3Days(address: string) {
  try {
    localStorage.setItem(suppressKey(address), String(Date.now() + SUPPRESS_MS))
  } catch {
    /* private mode — the nudge simply keeps its in-session silence */
  }
}

const nextDelay = () =>
  NUDGE_MIN_DELAY_MS + Math.floor(Math.random() * (NUDGE_MAX_DELAY_MS - NUDGE_MIN_DELAY_MS))

/** Screens where interrupting with a modal would be hostile — skip the tick, retry next cycle. */
function isExcludedRoute(pathname: string): boolean {
  const p = pathname.toLowerCase()
  return p.startsWith('/alliance') || p.includes('battle') || p.includes('arena') || p.includes('/pvp')
}

export const AllianceJoinNudgeHost: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { address } = useWeb3Context()
  const userLvl = useSelector((state: RootState) => state.userModule?.user?.userLvl) ?? 1

  const [open, setOpen] = useState(false)

  // Refs so the timer chain reads live values without re-arming on every change.
  const addressRef = useRef(address)
  addressRef.current = address
  const levelRef = useRef(userLvl)
  levelRef.current = userLvl
  const pathRef = useRef(location.pathname)
  pathRef.current = location.pathname
  const openRef = useRef(open)
  openRef.current = open

  const tick = useCallback(async () => {
    const wallet = addressRef.current
    if (!wallet) return
    if (openRef.current) return
    if ((levelRef.current ?? 1) < MIN_LEVEL_JOIN) return
    if (isSuppressed(wallet)) return
    if (isExcludedRoute(pathRef.current)) return
    try {
      const inAlliance = await checkIsWalletInAlliance(wallet)
      if (!inAlliance && addressRef.current === wallet) setOpen(true)
    } catch {
      /* offline / transient error — try again next cycle */
    }
  }, [])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null
    let disposed = false
    const arm = () => {
      if (disposed) return
      timer = setTimeout(async () => {
        await tick()
        arm()
      }, nextDelay())
    }
    arm()
    return () => {
      disposed = true
      if (timer) clearTimeout(timer)
    }
  }, [tick])

  const close = useCallback(() => setOpen(false), [])

  const goToAlliance = useCallback(() => {
    setOpen(false)
    navigate('/alliance')
  }, [navigate])

  const dontShowFor3Days = useCallback(() => {
    const wallet = addressRef.current
    if (wallet) suppressFor3Days(wallet)
    setOpen(false)
  }, [])

  if (!address) return null

  return (
    <ConfirmModal
      open={open}
      onClose={close}
      onConfirm={goToAlliance}
      title={t('alliance.nudgeTitle', { defaultValue: 'Join an Alliance' })}
      message={t('alliance.nudgeMessage', {
        defaultValue: 'Would you like to join an alliance? Please join or create an alliance.',
      })}
      confirmText={t('modals.confirmModal.defaultConfirm', { defaultValue: 'OK' })}
      cancelText={t('modals.confirmModal.defaultCancel', { defaultValue: 'Cancel' })}
      secondaryText={t('alliance.nudgeDontShow3Days', { defaultValue: "Don't show for 3 days" })}
      onSecondary={dontShowFor3Days}
    />
  )
}

export default AllianceJoinNudgeHost
